import type { PresencaChamada } from '@arena/shared/dominio';
import {
  lotacaoDaTurma,
  ROTULO_MOTIVO_DE_SAIDA,
  type GradeDaTurma,
  type Lotacao,
  type MotivoDeSaida,
} from '@arena/shared/schemas';
import { horaDe, minutosDe } from '@arena/shared/tempo';
import { horaCurta } from '../agenda/formatos.js';

/**
 * Os textos e cores de turmas. Funções puras.
 *
 * A régua do semáforo NÃO mora aqui: `lotacaoDaTurma` é de
 * `@arena/shared`, a mesma que o servidor usa. Aqui só a cor que cada
 * faixa recebe — e ela vem dos tokens (`--lotacao-*`), nunca de um hex
 * solto.
 */

export const COR_DA_LOTACAO: Readonly<Record<Lotacao, string>> = {
  folgada: 'var(--lotacao-folgada)',
  media: 'var(--lotacao-media)',
  cheia: 'var(--lotacao-cheia)',
};

export function corDaLotacao(matriculados: number, capacidade: number): string {
  return COR_DA_LOTACAO[lotacaoDaTurma(matriculados, capacidade)];
}

const NOMES_DOS_DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const NOMES_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** [2, 4] → "Terça e quinta" — como os cards de `#v-turmas`. */
export function diasDaTurma(dias: readonly number[]): string {
  const nomes = dias.map((d, i) => {
    const nome = NOMES_DOS_DIAS[d] ?? '?';
    return i === 0 ? nome : nome.toLowerCase();
  });
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

/** [2, 4] → "ter/qui" — o "vôlei ter/qui 19h" do card de risco. */
export function diasCurtos(dias: readonly number[]): string {
  return dias.map((d) => NOMES_CURTOS[d] ?? '?').join('/');
}

/** "19:00" + 60 → "19h às 20h". */
export function horarioDaTurma(horaInicio: string, duracaoMinutos: number): string {
  return `${horaCurta(horaInicio)} às ${horaCurta(horaDe(minutosDe(horaInicio) + duracaoMinutos))}`;
}

/** "Terça e quinta · 19h às 20h", ou o aviso de que a grade está fechada. */
export function gradeResumida(grade: GradeDaTurma | null): string {
  if (!grade) return 'Sem grade na agenda';
  return `${diasDaTurma(grade.diasSemana)} · ${horarioDaTurma(grade.horaInicio, grade.duracaoMinutos)}`;
}

export const ROTULO_ESTADO_NA_CHAMADA: Readonly<Record<PresencaChamada, string>> = {
  Presente: 'Presente',
  Falta: 'Falta',
  Reposicao: 'Reposição',
};

/** A cor de cada botão da chamada — a do protótipo: verde, vermelho, azul. */
export const COR_DO_ESTADO: Readonly<Record<PresencaChamada, string>> = {
  Presente: 'var(--green)',
  Falta: 'var(--red)',
  Reposicao: 'var(--blue)',
};

/** "saiu em 02/06/2026 · transferência de turma". */
export function saidaPorExtenso(saiuEm: string, motivo: MotivoDeSaida | null): string {
  const data = `${saiuEm.slice(8, 10)}/${saiuEm.slice(5, 7)}/${saiuEm.slice(0, 4)}`;
  return motivo ? `saiu em ${data} · ${ROTULO_MOTIVO_DE_SAIDA[motivo]}` : `saiu em ${data}`;
}

/** "12/03/2026". */
export function dataCompleta(data: string): string {
  return `${data.slice(8, 10)}/${data.slice(5, 7)}/${data.slice(0, 4)}`;
}

/** "há 9 dias", "hoje", "há 1 dia". */
export function haDias(dias: number): string {
  if (dias <= 0) return 'desde hoje';
  return dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
}
